// base
import React, { Suspense, useContext, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

// layouts
import { ErrorBoundary } from 'layouts';

// components
import { AuthorizedResult, NotFoundResult, SplashLoader } from 'components';

// modules
import { LoadingAtom } from 'modules';
import { useAppSelector } from 'modules/store';

// hooks
import { useUser } from 'hooks';

// routes
import { routes } from 'routes';
import { PrivateRoute } from 'routes/PrivateRoute';

// enums
import { checkUserRole, getUserRole, UserRole } from 'enums';

// libraries
import { useRecoilValue } from 'recoil';
import {
  ROUTE_3D_DASHBOARD,
  ROUTE_CHART_DASHBOARD,
  ROUTE_MDM_DEVICES,
  ROUTE_SIGN_IN
} from 'routes/const.js';
import { LazyMdmDeviceListPage } from 'pages';
import styled from 'styled-components';
import { useFcm } from './fcm.js';
import { LightningAlert } from './containers/Dashboard/Ptw/components/lightningAlert';
import { PushContext } from 'contexts';
import { LightningAlarm } from './containers/Dashboard/Ptw/components/lightningAlarm';

const StyledApp = styled.div`
  height: 100vh;
  position: relative;
`;

const App: React.FC = () => {
  const isLoading = useRecoilValue(LoadingAtom);
  const { pageLoading } = useAppSelector((state) => state.ui);

  const { isLogin, userInfo, userRole } = useUser();
  const { wsWeather, isAlarmOpen, openAlarm, closeAlarm } = useContext(PushContext);
  useFcm();

  const checkUser = (userRole: UserRole | null) => {
    if (!userRole) return false;

    const userRoles = getUserRole();
    return userRoles.some((item) => userRole === item);
  };

  const returnNoLogin = useMemo(() => {
    if (!isLogin || !userInfo || !userRole) {
      return (
        <Switch>
          {routes.public.map((item, index) => (
            <Route key={index} path={item.path} component={item.component} exact />
          ))}
          <Redirect to={ROUTE_SIGN_IN} />
        </Switch>
      );
    }
  }, [isLogin, userInfo, userRole]);

  return (
    <>
      <ErrorBoundary>
        {(pageLoading || isLoading) && <SplashLoader />}
        <StyledApp id="app">
          <Suspense fallback={<SplashLoader />}>
            <LightningAlert data={wsWeather} onOk={openAlarm} />
            <LightningAlarm show={isAlarmOpen} onClose={closeAlarm} />
            {returnNoLogin || (
              <Switch>
                <Route path={ROUTE_SIGN_IN} exact>
                  <Redirect to={ROUTE_3D_DASHBOARD} />
                </Route>

                <Route path={ROUTE_MDM_DEVICES} exact component={LazyMdmDeviceListPage} />

                {routes.common.map((item, index) => (
                  <PrivateRoute
                    key={index}
                    path={item.path}
                    fallback={AuthorizedResult}
                    isPermission={checkUser}
                    component={item.component}
                    exact
                  />
                ))}

                {userRole &&
                  checkUserRole(userRole) &&
                  routes[userRole.replace('ROLE_', '')]?.map((item, index) => (
                    <PrivateRoute
                      key={index}
                      path={item.path}
                      fallback={AuthorizedResult}
                      isPermission={checkUser}
                      component={item.component}
                      exact
                    />
                  ))}

                {!userRole &&
                  routes.common.map((item, index) => (
                    <PrivateRoute
                      key={index}
                      path={item.path}
                      fallback={AuthorizedResult}
                      isPermission={checkUser}
                      component={item.component}
                      exact
                    />
                  ))}
                <Route path="*" component={NotFoundResult} />
              </Switch>
            )}
          </Suspense>
        </StyledApp>
      </ErrorBoundary>
    </>
  );
};

export default App;
